/**
 * CharacterCreationFlow.ts
 * 
 * Terra Anima (테라 아니마) - 정통 신규 캐릭터 생성 플로우
 * 
 * 6단계 마법/성인식 프로세스:
 *  Step 1: 세계관 & 4중 항성계 전설 (Lore & Background)
 *  Step 2: [영혼의 성소] 12세 성인식 의식 (Coming of Age Ceremony)
 *  Step 3: 8문 8답 성향 시험 (8-Question Personality Sanctuary - 고정 높이 덜컹거림 제거)
 *  Step 4: 종족 선택 (6대 종족) & 스탯 보너스 30 배분 (추천 배분은 버튼 눌렀을 때만 적용)
 *  Step 5: 캐릭터 외형 커스터마이징 (얼굴/피부/헤어/갑옷/망토/투구/체형 실시간 전신 렌더링) & 이름 중복 확인
 *  Step 6: 캐릭터 완성 확인 (Summary) -> 솔라리스 왕국 진입
 */

import {
  RaceId,
  EntityRole,
  StatBlock,
  PersonalityProfile,
  CharacterAppearance,
  EntityState,
  GenderType,
  BodyShapeType,
  EyeAngleType,
  EyeSizeType,
  HairStyleId,
  FacialFeatureId,
} from '../../core/Types';
import { AccountSaveSystem } from '../../core/AccountSaveSystem';
import {
  TERRA_ANIMA_RACES,
  SKIN_COLOR_PALETTE,
  HAIR_COLOR_PALETTE,
  MALE_HAIR_STYLES,
  FEMALE_HAIR_STYLES,
  EYE_ANGLE_OPTIONS,
  EYE_SIZE_OPTIONS,
  BODY_SHAPE_OPTIONS,
  FACIAL_FEATURE_OPTIONS,
  randomizeCharacterAppearance,
} from '../character/CharacterSystem';
import {
  PERSONALITY_QUESTIONS,
  computePersonalityProfile,
} from '../character/PersonalityQuiz';
import { SpriteRenderer } from '../view/SpriteRenderer';
import { WorldClock } from '../world/WorldClock';

export interface CharacterCreationResult {
  name: string;
  race: RaceId;
  role: EntityRole;
  job: string;
  stats: StatBlock;
  personality: PersonalityProfile;
  appearance: CharacterAppearance;
}

export class CharacterCreationFlow {
  private container: HTMLElement | null = null;
  private currentStep = 1;
  private totalSteps = 6;

  // State data
  private selectedRace: RaceId = 'solarian';
  private selectedRole: EntityRole = 'tank';
  // 2번 요청 반영: 기본 보너스 30pt 전부 잔여 상태로 시작 (0으로 자동할당 금지)
  private allocatedStats: StatBlock = { str: 0, con: 0, dex: 0, qui: 0, int: 0, pie: 0, cha: 0 };
  private remainingBonusPoints = 30;

  private selectedGender: GenderType = 'male';
  private quizAnswers: number[] = [0, 0, 0, 0, 0, 0, 0, 0];
  private currentQuizIdx = 0;
  private appearance: CharacterAppearance = randomizeCharacterAppearance('male');
  private characterName = '아니마의모험가';
  private nameChecked = false;

  // 5번 요청: 캔버스 전신 렌더러
  private spriteRenderer: SpriteRenderer = new SpriteRenderer();
  private previewAnimFrame: number | null = null;

  private onCompleteCallback?: (result: CharacterCreationResult) => void;
  private onCancelCallback?: () => void;
  private onResizeBound = () => this.adjustResponsiveLayout();

  constructor() {
    this.appearance.gender = 'male';
    this.appearance.showHelm = false; // 기본 얼굴 및 헤어가 잘 보이도록 투구 해제
    this.appearance.skinColor = '#fde68a';
    this.appearance.hairColor = '#facc15';
    this.appearance.hairStyle = 'short';
    this.appearance.facialFeature = 'none';
    this.appearance.primaryArmorColor = '#b45309'; // 기본 황토색 속옷 고정
    this.appearance.secondaryArmorColor = '#92400e';
    this.appearance.bodyShape = 'normal';
    this.appearance.eyeAngle = 'normal';
    this.appearance.eyeSize = 'medium';
  }

  public start(
    onComplete: (result: CharacterCreationResult) => void,
    onCancel: () => void
  ): void {
    this.onCompleteCallback = onComplete;
    this.onCancelCallback = onCancel;
    this.currentStep = 1;
    this.createDom();
    this.render();
  }

  private createDom(): void {
    if (this.container) {
      this.container.remove();
    }

    const flowEl = document.createElement('div');
    flowEl.id = 'char-create-flow';
    flowEl.style.position = 'fixed';
    flowEl.style.inset = '0';
    flowEl.style.zIndex = '9000';
    flowEl.style.display = 'flex';
    flowEl.style.flexDirection = 'column';
    flowEl.style.background = 'radial-gradient(ellipse at center, #0b1329 0%, #030712 100%)';
    flowEl.style.color = '#f8fafc';
    flowEl.style.fontFamily = '"Malgun Gothic", -apple-system, sans-serif';
    flowEl.style.userSelect = 'none';
    flowEl.style.overflow = 'hidden';

    document.body.appendChild(flowEl);
    this.container = flowEl;

    // 모바일 터치 & 반응형 스타일 주입
    this.injectStyles();
    
    // 모바일 회전 및 리사이즈 감지
    window.addEventListener('resize', this.onResizeBound);
  }

  private injectStyles(): void {
    if (document.getElementById('char-create-responsive-styles')) return;

    const styleEl = document.createElement('style');
    styleEl.id = 'char-create-responsive-styles';
    styleEl.textContent = `
      /* 공통 터치 버튼 피드백 */
      .cc-touch-btn:active {
        transform: scale(0.98);
        filter: brightness(1.15);
      }
      
      /* 커스텀 스크롤바 */
      .cc-scroll::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }
      .cc-scroll::-webkit-scrollbar-track {
        background: rgba(15, 23, 42, 0.6);
        border-radius: 4px;
      }
      .cc-scroll::-webkit-scrollbar-thumb {
        background: #475569;
        border-radius: 4px;
      }
      .cc-scroll::-webkit-scrollbar-thumb:hover {
        background: #f59e0b;
      }

      /* 모바일 전용 세로 표준화 (화면 너비 768px 이하) */
      @media (max-width: 768px) {
        #char-create-flow header {
          padding: 8px 12px !important;
          min-height: 52px !important;
        }
        #char-create-flow header h1 {
          font-size: 14px !important;
        }
        #char-create-flow header p {
          font-size: 10px !important;
        }
        #char-create-flow footer {
          padding: 8px 12px !important;
          min-height: 56px !important;
        }
        #btn-create-prev, #btn-create-next {
          padding: 10px 16px !important;
          font-size: 14px !important;
          min-height: 42px !important;
        }

        #create-body {
          align-items: flex-start !important;
          justify-content: center !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          -webkit-overflow-scrolling: touch !important;
          padding: 12px 10px 24px 10px !important;
        }

        .cc-card-container {
          width: 100% !important;
          max-width: 480px !important;
          height: auto !important;
          min-height: auto !important;
          padding: 14px !important;
          transform: none !important;
          gap: 14px !important;
          box-sizing: border-box !important;
        }

        /* Step 1: 세계관 */
        .cc-lore-top {
          flex-direction: column !important;
          gap: 12px !important;
        }
        .cc-lore-img {
          width: 100% !important;
          max-width: 100% !important;
          height: 180px !important;
        }
        .cc-lore-cards {
          grid-template-columns: 1fr !important;
        }

        /* Step 2: 성인식 */
        .cc-ceremony-box {
          padding: 18px 14px !important;
        }

        /* Step 3: 성소 문답 */
        .cc-quiz-box {
          height: auto !important;
          min-height: 70px !important;
          padding: 14px !important;
          font-size: 14.5px !important;
        }
        .cc-quiz-opt {
          min-height: 52px !important;
          height: auto !important;
          padding: 10px 12px !important;
        }

        /* Step 4: 종족 & 스탯 */
        .cc-step4-layout {
          flex-direction: column !important;
          height: auto !important;
          gap: 14px !important;
        }
        .cc-step4-races {
          width: 100% !important;
          height: auto !important;
        }
        .cc-step4-race-list {
          display: grid !important;
          grid-template-columns: 1fr 1fr !important;
          gap: 6px !important;
        }
        .cc-step4-stats {
          width: 100% !important;
          height: auto !important;
          padding: 12px !important;
        }
        .cc-stat-row {
          padding: 8px 10px !important;
        }
        .cc-stat-btn {
          width: 32px !important;
          height: 32px !important;
          font-size: 16px !important;
        }

        /* Step 5: 커스터마이징 */
        .cc-step5-layout {
          flex-direction: column !important;
          height: auto !important;
          gap: 14px !important;
        }
        .cc-step5-avatar {
          width: 100% !important;
          height: auto !important;
          padding: 14px !important;
        }
        .cc-step5-canvas-wrap {
          width: 200px !important;
          height: 240px !important;
        }
        .cc-step5-canvas {
          width: 200px !important;
          height: 240px !important;
        }
        .cc-step5-controls {
          width: 100% !important;
          height: auto !important;
          padding: 14px !important;
          gap: 12px !important;
        }
        .cc-step5-name-gender {
          flex-direction: column !important;
        }
        .cc-step5-gender-box {
          width: 100% !important;
        }
        .cc-step5-grid {
          grid-template-columns: 1fr !important;
          gap: 8px !important;
        }

        /* Step 6: 완료 확인 */
        .cc-step6-grid {
          grid-template-columns: 1fr !important;
        }
        .cc-step6-stats {
          flex-wrap: wrap !important;
          gap: 8px !important;
        }
        .cc-step6-stat-item {
          width: calc(33.3% - 6px) !important;
        }
      }
    `;
    document.head.appendChild(styleEl);
  }

  public close(): void {
    window.removeEventListener('resize', this.onResizeBound);
    if (this.previewAnimFrame !== null) {
      cancelAnimationFrame(this.previewAnimFrame);
      this.previewAnimFrame = null;
    }
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }

  private render(): void {
    if (!this.container) return;

    if (this.previewAnimFrame !== null) {
      cancelAnimationFrame(this.previewAnimFrame);
      this.previewAnimFrame = null;
    }

    this.container.innerHTML = `
      <!-- Top Step Header (모바일 반응형 패딩 & 높이) -->
      <header style="
        min-height: 54px; flex-shrink: 0; display: flex; align-items: center; justify-content: space-between;
        padding: 6px 16px; background: rgba(15, 23, 42, 0.95);
        border-bottom: 2px solid #d97706; backdrop-filter: blur(8px); gap: 8px;
      ">
        <div style="display: flex; align-items: center; gap: 8px; min-width: 0;">
          <span style="font-size: 20px; flex-shrink: 0;">✨</span>
          <div style="min-width: 0;">
            <h1 style="font-size: 15px; font-weight: 800; color: #fde047; letter-spacing: 0.3px; margin: 0; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
              테라 아니마 · 신규 캐릭터 생성
            </h1>
            <p style="font-size: 11px; color: #94a3b8; margin: 0; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
              단계 ${this.currentStep} / ${this.totalSteps} — ${this.getStepTitle(this.currentStep)}
            </p>
          </div>
        </div>

        <!-- Stepper Dots (모바일 컴팩트) -->
        <div style="display: flex; align-items: center; gap: 4px; flex-shrink: 0;">
          ${Array.from({ length: this.totalSteps }, (_, i) => i + 1)
            .map(
              (s) => `
              <div style="
                width: 24px; height: 24px; border-radius: 50%;
                display: flex; align-items: center; justify-content: center;
                font-size: 11px; font-weight: bold;
                background: ${s === this.currentStep ? '#f59e0b' : s < this.currentStep ? '#10b981' : '#1e293b'};
                color: ${s === this.currentStep ? '#000' : '#cbd5e1'};
                border: 1.5px solid ${s === this.currentStep ? '#fde047' : s < this.currentStep ? '#34d399' : '#334155'};
                box-shadow: ${s === this.currentStep ? '0 0 8px rgba(245, 158, 11, 0.6)' : 'none'};
                transition: all 0.2s;
              ">${s < this.currentStep ? '✓' : s}</div>
            `
            )
            .join('<div style="width: 8px; height: 2px; background: #334155;"></div>')}
        </div>

        <button id="btn-create-exit" style="
          background: #1e293b; border: 1px solid #475569; color: #cbd5e1;
          padding: 5px 10px; border-radius: 6px; cursor: pointer; font-size: 11.5px; flex-shrink: 0;
        ">✕ 메인</button>
      </header>

      <!-- Center Body Content -->
      <main id="create-body" class="cc-scroll" style="
        flex: 1; display: flex; align-items: center; justify-content: center;
        padding: 8px; overflow: hidden;
      "></main>

      <!-- Bottom Nav Footer (모바일 반응형) -->
      <footer style="
        min-height: 50px; flex-shrink: 0; display: flex; align-items: center; justify-content: space-between;
        padding: 6px 16px; background: rgba(15, 23, 42, 0.95);
        border-top: 1px solid #1e293b; gap: 8px;
      ">
        <button id="btn-create-prev" style="
          background: ${this.currentStep === 1 ? '#334155' : '#1e293b'};
          border: 1px solid #475569; color: ${this.currentStep === 1 ? '#64748b' : '#f8fafc'};
          padding: 6px 14px; border-radius: 6px; font-weight: bold; cursor: ${this.currentStep === 1 ? 'not-allowed' : 'pointer'};
          font-size: 12.5px; flex-shrink: 0;
        " ${this.currentStep === 1 ? 'disabled' : ''}>◀ 이전</button>

        <div style="font-size: 11px; color: #94a3b8; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
          Miraero Games · 테라 아니마
        </div>

        <button id="btn-create-next" style="
          background: linear-gradient(135deg, #d97706, #f59e0b);
          border: 1.5px solid #fde047; color: #000; font-weight: 800;
          padding: 6px 18px; border-radius: 6px; cursor: pointer; font-size: 12.5px;
          box-shadow: 0 4px 14px rgba(245, 158, 11, 0.4); flex-shrink: 0;
        ">${this.currentStep === this.totalSteps ? '⚔️ 월드 진입' : '다음 ▶'}</button>
      </footer>
    `;

    // Hook buttons
    this.container.querySelector('#btn-create-exit')?.addEventListener('click', () => {
      if (confirm('캐릭터 생성을 중단하고 메인 메뉴로 돌아가시겠습니까?')) {
        this.close();
        this.onCancelCallback?.();
      }
    });

    this.container.querySelector('#btn-create-prev')?.addEventListener('click', () => {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.render();
      }
    });

    this.container.querySelector('#btn-create-next')?.addEventListener('click', () => {
      if (this.currentStep < this.totalSteps) {
        // Step 4 validate
        if (this.currentStep === 4 && this.remainingBonusPoints > 0) {
          alert(`보너스 스탯 ${this.remainingBonusPoints}포인트가 아직 남아있습니다. 모두 분배해주세요!`);
          return;
        }
        if (this.currentStep === 5) {
          const nm = this.characterName.trim();
          if (!nm) {
            alert('캐릭터 이름을 입력해주세요!');
            return;
          }
          if (nm.length < 2) {
            alert('캐릭터 이름은 2글자 이상이어야 합니다.');
            return;
          }
          if (!this.nameChecked || AccountSaveSystem.isNameTaken(nm)) {
            this.nameChecked = false;
            alert('이름 [중복 확인]을 먼저 해주세요. (이미 사용 중인 이름은 사용할 수 없습니다)');
            return;
          }
        }
        this.currentStep++;
        this.render();
      } else {
        this.finishCreation();
      }
    });

    const bodyEl = this.container.querySelector('#create-body') as HTMLElement;
    if (bodyEl) {
      this.renderCurrentStep(bodyEl);
      this.adjustResponsiveLayout();
    }
  }

  /**
   * 모바일(<= 768px)에서는 transform scale을 절대 적용하지 않고 유동 반응형 세로 레이아웃 유지.
   * PC/태블릿 데스크톱 뷰포트에서 창 높이가 낮을 경우에만 미세 scale 축소.
   */
  private adjustResponsiveLayout(): void {
    if (!this.container) return;
    const bodyEl = this.container.querySelector('#create-body') as HTMLElement;
    const stepCard = bodyEl?.firstElementChild as HTMLElement;
    if (!stepCard) return;

    // 모바일 세로/표준화 뷰포트에서는 scale 전면 해제 -> 온전한 글씨 크기와 세로 터치 스크롤 제공
    if (window.innerWidth <= 768) {
      stepCard.style.transform = 'none';
      return;
    }

    // 데스크톱 뷰포트
    const availW = window.innerWidth - 32;
    const availH = window.innerHeight - 130;

    const baseW = stepCard.offsetWidth || 1020;
    const baseH = stepCard.offsetHeight || 510;

    const scaleX = availW / baseW;
    const scaleY = availH / baseH;
    const fitScale = Math.min(1, scaleX, scaleY);

    if (fitScale < 0.98) {
      stepCard.style.transform = `scale(${fitScale.toFixed(3)})`;
      stepCard.style.transformOrigin = 'center center';
    } else {
      stepCard.style.transform = 'none';
    }
  }

  private getStepTitle(step: number): string {
    switch (step) {
      case 1: return '세계관 & 4중 항성계 전설 (Lore & Background)';
      case 2: return '12세 성인식 의식 (Coming-of-Age Ceremony)';
      case 3: return '영혼의 성소 8문 8답 성향 시험 (Sanctuary Quiz)';
      case 4: return '종족 선택 & 보너스 능력치 배분 (Race & Stats)';
      case 5: return '외형 커스터마이징 & 이름 짓기 (Appearance & Name)';
      case 6: return '캐릭터 생성 완료 및 월드 진입 확인 (Confirmation)';
      default: return '';
    }
  }

  private renderCurrentStep(container: HTMLElement): void {
    switch (this.currentStep) {
      case 1: this.renderStep1Lore(container); break;
      case 2: this.renderStep2Ceremony(container); break;
      case 3: this.renderStep3Sanctuary(container); break;
      case 4: this.renderStep4RaceAndStats(container); break;
      case 5: this.renderStep5Customization(container); break;
      case 6: this.renderStep6Confirmation(container); break;
    }
  }

  // --------------------------------------------------------------------------
  // STEP 1: 세계관 & 4중 항성계 전설 (PC: 900px 가로형 / 모바일: 세로 스택)
  // --------------------------------------------------------------------------
  private renderStep1Lore(el: HTMLElement): void {
    el.innerHTML = `
      <div class="cc-card-container" style="
        max-width: 900px; width: 100%; display: flex; flex-direction: column;
        gap: 16px; background: rgba(15, 23, 42, 0.95); border: 1.5px solid #334155;
        border-radius: 12px; padding: 22px 28px; box-shadow: 0 10px 30px rgba(0,0,0,0.5);
      ">
        <div class="cc-lore-top" style="display: flex; gap: 20px; align-items: center;">
          <img src="/assets/splash.jpg" alt="Solaris World" class="cc-lore-img" style="
            width: 300px; height: 180px; object-fit: cover; border-radius: 8px;
            border: 2px solid #d97706; box-shadow: 0 4px 16px rgba(0,0,0,0.6); flex-shrink: 0;
          " />
          <div style="flex: 1;">
            <span style="font-size: 11px; font-weight: bold; color: #f59e0b; letter-spacing: 1.5px; text-transform: uppercase;">
              TERRA ANIMA WORLD CHRONICLES
            </span>
            <h2 style="font-size: 20px; color: #fef08a; margin: 4px 0 8px; line-height: 1.3;">
              네 개의 태양과 두 개의 달이 비추는 대륙, 솔라리스
            </h2>
            <p style="font-size: 13px; line-height: 1.6; color: #cbd5e1; margin-bottom: 8px;">
              태초의 창조주가 솔라리스 행성에 네 개의 항성을 배치했을 때, 세상은 영원히 꺼지지 않는 황금빛 축복을 받았습니다.
              그러나 4태양이 본래의 천체 정렬 위치로 돌아오려면 <b>10번의 계절 순환</b>을 거쳐야 공식 <b>1살</b>을 먹는 기나긴 세월이 필요합니다.
            </p>
            <p style="font-size: 12.5px; line-height: 1.5; color: #94a3b8; margin: 0;">
              당신은 오랜 계절 동안 수련을 마치고 마침내 <b>12세 성인식</b>을 앞두고 있습니다.
              왕국의 영혼의 성소에서 당신의 내면을 밝히고, 운명의 길을 선택할 시간입니다.
            </p>
          </div>
        </div>

        <div class="cc-lore-cards" style="
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
          margin-top: 4px; padding-top: 14px; border-top: 1px solid #1e293b;
        ">
          <div style="background: #1e293b; padding: 12px; border-radius: 6px; border: 1px solid #334155;">
            <div style="font-size: 14px; font-weight: bold; color: #fef08a; margin-bottom: 4px;">☀️ 4중 항성계 (4 Suns)</div>
            <div style="font-size: 11.5px; color: #cbd5e1; line-height: 1.45;">
              네 항성의 궤도에 의해 시간과 계절이 결정되며, 낮과 밤의 주기 속에서 신비로운 마나의 흐름이 생성됩니다.
            </div>
          </div>
          <div style="background: #1e293b; padding: 12px; border-radius: 6px; border: 1px solid #334155;">
            <div style="font-size: 14px; font-weight: bold; color: #fef08a; margin-bottom: 4px;">🌙 2개의 쌍둥이 달</div>
            <div style="font-size: 11.5px; color: #cbd5e1; line-height: 1.45;">
              하늘 높이 뜬 백은의 달과 자수정빛 달은 모험가들의 영혼에 잠재된 초자연적 감응력과 신성 마법을 증폭시킵니다.
            </div>
          </div>
          <div style="background: #1e293b; padding: 12px; border-radius: 6px; border: 1px solid #334155;">
            <div style="font-size: 14px; font-weight: bold; color: #fef08a; margin-bottom: 4px;">🏛️ 솔라리움 왕국 성채</div>
            <div style="font-size: 11.5px; color: #cbd5e1; line-height: 1.45;">
              인간, 북방민족, 숲의 아이들, 사막의 유목민 등 6대 종족이 공존하는 정통 판타지 대륙의 거대한 수도입니다.
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // --------------------------------------------------------------------------
  // STEP 2: 12세 성인식 의식 (모바일 세로 규격 대응)
  // --------------------------------------------------------------------------
  private renderStep2Ceremony(el: HTMLElement): void {
    el.innerHTML = `
      <div class="cc-card-container cc-ceremony-box" style="
        max-width: 780px; width: 100%; display: flex; flex-direction: column;
        gap: 16px; background: rgba(15, 23, 42, 0.95); border: 1.5px solid #d97706;
        border-radius: 12px; padding: 28px 32px; box-shadow: 0 10px 30px rgba(0,0,0,0.5);
        text-align: center;
      ">
        <div style="font-size: 38px; line-height: 1;">🕯️ 🏛️ 🕯️</div>
        <h2 style="font-size: 21px; color: #fef08a; margin: 0; line-height: 1.3;">
          [영혼의 성소] 12세 성인식 (Coming of Age Ceremony)
        </h2>
        <div style="width: 100px; height: 2px; background: #f59e0b; margin: 0 auto;"></div>

        <p style="font-size: 13.5px; line-height: 1.7; color: #e2e8f0; max-width: 620px; margin: 0 auto;">
          "아이여, 그대가 태어난 지 열 번의 계절 순환이 열두 번 지나 성인의 문턱에 도달하였도다.<br />
          이곳 영혼의 성소 제단 위에 손을 얹고, 네 심장 가장 깊은 곳의 목소리에 귀를 기울여라.<br />
          사제들이 묻는 여덟 가지 문답을 통해 네 영혼의 성향과 타고난 운명이 기록될 것이니라."
        </p>

        <div style="
          background: #1e293b; border: 1px solid #334155; border-radius: 8px;
          padding: 16px 20px; margin: 6px auto; width: 100%; max-width: 560px; text-align: left; box-sizing: border-box;
        ">
          <div style="font-size: 13px; font-weight: bold; color: #fde047; margin-bottom: 6px;">
            📜 성인식의 3대 계명:
          </div>
          <ul style="font-size: 12px; color: #94a3b8; line-height: 1.6; padding-left: 18px; margin: 0;">
            <li>거짓 없이 자신의 마음에 진실하게 답할 것</li>
            <li>여덟 가지 축(용기, 질서, 온정, 지혜 등)의 성향은 향후 파티 상성과 NPC 관계에 영향을 미침</li>
            <li>성인식을 마친 모험가는 솔라리스 왕국의 어엿한 시민으로서 칭호와 권리를 부여받음</li>
          </ul>
        </div>

        <div>
          <button id="btn-enter-sanctuary" class="cc-touch-btn" style="
            background: linear-gradient(135deg, #b45309, #d97706);
            border: 1.5px solid #fde047; color: #fff; font-weight: bold;
            padding: 12px 28px; border-radius: 8px; cursor: pointer; font-size: 14px;
            box-shadow: 0 4px 14px rgba(245, 158, 11, 0.4); min-height: 44px;
          ">🕯️ 영혼의 문답 시험 시작하기</button>
        </div>
      </div>
    `;

    el.querySelector('#btn-enter-sanctuary')?.addEventListener('click', () => {
      this.currentStep = 3;
      this.render();
    });
  }

  // --------------------------------------------------------------------------
  // STEP 3: 영혼의 성소 8문 8답 시험 (PC: 820px / 모바일: 세로 표준 유동 너비)
  // --------------------------------------------------------------------------
  private renderStep3Sanctuary(el: HTMLElement): void {
    const q = PERSONALITY_QUESTIONS[this.currentQuizIdx];
    const currentAns = this.quizAnswers[this.currentQuizIdx];

    el.innerHTML = `
      <div class="cc-card-container" style="
        width: 100%; max-width: 820px; min-height: 500px; display: flex; flex-direction: column;
        justify-content: space-between; background: rgba(15, 23, 42, 0.95); border: 1.5px solid #f59e0b;
        border-radius: 12px; padding: 22px 28px; box-shadow: 0 10px 30px rgba(0,0,0,0.6);
        box-sizing: border-box; gap: 14px;
      ">
        <!-- Top: Progress Indicator -->
        <div style="display: flex; justify-content: space-between; align-items: center; min-height: 26px; flex-wrap: wrap; gap: 6px;">
          <span style="font-size: 13px; color: #fde047; font-weight: bold;">
            [영혼의 성소 문답] 제 ${this.currentQuizIdx + 1}문 / 8문 — ${q.axisNameKr}
          </span>
          <div style="display: flex; gap: 4px;">
            ${PERSONALITY_QUESTIONS.map(
              (_: unknown, idx: number) => `
              <div style="
                width: 22px; height: 22px; border-radius: 50%;
                background: ${idx === this.currentQuizIdx ? '#f59e0b' : this.quizAnswers[idx] !== undefined ? '#10b981' : '#334155'};
                font-size: 11px; display: flex; align-items: center; justify-content: center;
                color: #fff; font-weight: bold;
              ">${idx + 1}</div>
            `
            ).join('')}
          </div>
        </div>

        <!-- Question Box (PC: 고정 높이 / 모바일: 가변 높이) -->
        <div class="cc-quiz-box" style="
          min-height: 76px; padding: 14px 18px; background: #0f172a; border: 1px solid #334155;
          border-radius: 8px; font-size: 15px; font-weight: bold; color: #fef08a; line-height: 1.5;
          display: flex; align-items: center; box-sizing: border-box;
        ">
          Q${q.id}. "${q.question}"
        </div>

        <!-- 4 Options (모바일 시원한 터치 타깃) -->
        <div style="display: flex; flex-direction: column; gap: 8px; flex: 1;">
          ${q.options
            .map(
              (opt: { text: string; score: number }, optIdx: number) => `
            <div data-opt-idx="${optIdx}" style="
              min-height: 52px; display: flex; align-items: center; gap: 14px; padding: 10px 16px;
              background: ${currentAns === optIdx ? 'linear-gradient(135deg, #1e3a8a, #1e293b)' : '#1e293b'};
              border: 1.5px solid ${currentAns === optIdx ? '#f59e0b' : '#334155'};
              border-radius: 8px; cursor: pointer; transition: all 0.15s; box-sizing: border-box;
            " class="quiz-opt-btn cc-touch-btn cc-quiz-opt">
              <span style="
                width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
                background: ${currentAns === optIdx ? '#f59e0b' : '#090d16'};
                color: ${currentAns === optIdx ? '#000' : '#cbd5e1'};
                font-weight: bold; font-size: 13px; display: flex; align-items: center; justify-content: center;
              ">${String.fromCharCode(65 + optIdx)}</span>
              <span style="font-size: 13.5px; color: ${currentAns === optIdx ? '#fef08a' : '#cbd5e1'}; line-height: 1.4;">
                ${opt.text}
              </span>
            </div>
          `
            )
            .join('')}
        </div>

        <!-- Bottom Controls -->
        <div style="display: flex; justify-content: space-between; align-items: center; min-height: 38px; gap: 8px;">
          <button id="btn-quiz-prev" class="cc-touch-btn" style="
            background: #1e293b; border: 1px solid #475569; color: #cbd5e1;
            padding: 8px 16px; border-radius: 6px; cursor: ${this.currentQuizIdx === 0 ? 'not-allowed' : 'pointer'};
            font-size: 13px;
          " ${this.currentQuizIdx === 0 ? 'disabled' : ''}>◀ 이전</button>

          <span style="font-size: 11px; color: #64748b; text-align: center;">
            문항 터치 시 자동 선택
          </span>

          <button id="btn-quiz-next" class="cc-touch-btn" style="
            background: ${this.currentQuizIdx === 7 ? 'linear-gradient(135deg, #10b981, #059669)' : '#1e293b'};
            border: 1.5px solid ${this.currentQuizIdx === 7 ? '#34d399' : '#475569'};
            color: #fff; font-weight: bold; padding: 8px 18px; border-radius: 6px; cursor: pointer; font-size: 13px;
          ">${this.currentQuizIdx === 7 ? '문답 완성 ▶' : '다음 ▶'}</button>
        </div>
      </div>
    `;

    el.querySelectorAll('.quiz-opt-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.getAttribute('data-opt-idx') || '0', 10);
        this.quizAnswers[this.currentQuizIdx] = idx;
        if (this.currentQuizIdx < 7) {
          this.currentQuizIdx++;
        }
        this.renderStep3Sanctuary(el);
      });
    });

    el.querySelector('#btn-quiz-prev')?.addEventListener('click', () => {
      if (this.currentQuizIdx > 0) {
        this.currentQuizIdx--;
        this.renderStep3Sanctuary(el);
      }
    });

    el.querySelector('#btn-quiz-next')?.addEventListener('click', () => {
      if (this.currentQuizIdx < 7) {
        this.currentQuizIdx++;
        this.renderStep3Sanctuary(el);
      } else {
        this.currentStep = 4;
        this.render();
      }
    });
  }

  // --------------------------------------------------------------------------
  // STEP 4: 종족 선택 & 스탯 보너스 30 배분 (PC: 1020px 가로 / 모바일: 세로 표준 규격)
  // --------------------------------------------------------------------------
  private renderStep4RaceAndStats(el: HTMLElement): void {
    const raceKeys = Object.keys(TERRA_ANIMA_RACES) as RaceId[];
    const curRaceDef = TERRA_ANIMA_RACES[this.selectedRace];

    el.innerHTML = `
      <div class="cc-card-container cc-step4-layout" style="
        display: flex; gap: 16px; width: 100%; max-width: 1020px; min-height: 510px;
        box-sizing: border-box;
      ">
        <!-- Left / Top: Race Selection List -->
        <div class="cc-step4-races" style="
          width: 340px; display: flex; flex-direction: column; gap: 8px;
          background: rgba(15, 23, 42, 0.95); border: 1px solid #334155;
          border-radius: 10px; padding: 14px; box-sizing: border-box;
        ">
          <div style="font-size: 14px; font-weight: bold; color: #fde047; margin-bottom: 2px;">
            1. 종족 선택 (6대 종족)
          </div>
          <div class="cc-step4-race-list" style="display: flex; flex-direction: column; gap: 6px; flex: 1;">
            ${raceKeys
              .map((rKey) => {
                const r = TERRA_ANIMA_RACES[rKey];
                const isSelected = rKey === this.selectedRace;
                return `
                  <div data-race-id="${rKey}" class="race-card-btn cc-touch-btn" style="
                    padding: 9px 12px; border-radius: 6px; cursor: pointer;
                    background: ${isSelected ? 'linear-gradient(135deg, #1e3a8a, #0f172a)' : '#1e293b'};
                    border: 1.5px solid ${isSelected ? '#f59e0b' : '#334155'};
                    transition: all 0.15s;
                  ">
                    <div style="display: flex; justify-content: space-between; align-items: center;">
                      <span style="font-weight: bold; font-size: 13.5px; color: ${isSelected ? '#fef08a' : '#f8fafc'};">
                        ${r.nameKr}
                      </span>
                      <span style="font-size: 10.5px; color: #94a3b8;">${r.originZone.split('/')[0].trim()}</span>
                    </div>
                    <div style="font-size: 11px; color: #94a3b8; margin-top: 2px; line-height: 1.35;">
                      ${r.description.slice(0, 48)}...
                    </div>
                  </div>
                `;
              })
              .join('')}
          </div>
        </div>

        <!-- Right / Bottom: Stat Allocation Table -->
        <div class="cc-step4-stats" style="
          flex: 1; display: flex; flex-direction: column; justify-content: space-between;
          background: rgba(15, 23, 42, 0.95); border: 1.5px solid #d97706;
          border-radius: 10px; padding: 16px 20px; box-sizing: border-box; gap: 10px;
        ">
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #334155; padding-bottom: 8px; flex-wrap: wrap; gap: 6px;">
            <div>
              <div style="font-size: 14.5px; font-weight: bold; color: #fef08a;">
                2. 능력치 분배 (STR · CON · DEX · QUI · INT · PIE · CHA)
              </div>
              <div style="font-size: 11.5px; color: #94a3b8;">
                ${curRaceDef.nameKr} 기본 스탯 420 + 자유 보너스 30 포인트
              </div>
            </div>
            <div style="
              background: #090d16; border: 1.5px solid #f59e0b; border-radius: 6px;
              padding: 6px 14px; font-size: 12.5px; font-weight: bold; color: #fde047;
            ">
              잔여 보너스: <span id="stat-remaining" style="font-size: 18px; color: ${this.remainingBonusPoints === 0 ? '#34d399' : '#f59e0b'};">${this.remainingBonusPoints}</span> PT
            </div>
          </div>

          <!-- 7 Stat Rows -->
          <div style="display: flex; flex-direction: column; gap: 6px; margin: 4px 0;">
            ${(
              [
                ['str', 'STR (힘)', '물리 공격력, 근접 무기 피해량, 소지 중량'],
                ['con', 'CON (체력)', '최대 생명력(HP), 피격 내성, HP 재생 속도'],
                ['dex', 'DEX (민첩)', '원거리 적중률, 활 공격력, 방패 방어율'],
                ['qui', 'QUI (순발력)', '무기 공격 속도, 캐스팅 시간 단축, 회피율'],
                ['int', 'INT (지능)', '비전 마법 피해량, 최대 마나(MP), 연금술 감응'],
                ['pie', 'PIE (신앙)', '신성 치유량, 신성 주문 방어력, 집중 풀(Conc)'],
                ['cha', 'CHA (매력)', '음유시인 버프 위력, NPC 친밀도, 상점 거래가 할인'],
              ] as const
            )
              .map(([key, label, desc]) => {
                const baseVal = curRaceDef.baseStats[key];
                const allocVal = this.allocatedStats[key];
                const totalVal = baseVal + allocVal;
                return `
                  <div class="cc-stat-row" style="
                    display: flex; align-items: center; justify-content: space-between;
                    background: #1e293b; padding: 7px 12px; border-radius: 6px; border: 1px solid #334155;
                  ">
                    <div style="flex: 1; min-width: 0;">
                      <div style="font-size: 13px; font-weight: bold; color: #f8fafc;">${label}</div>
                      <div style="font-size: 10px; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${desc}</div>
                    </div>
                    <div style="display: flex; align-items: center; gap: 6px; flex-shrink: 0;">
                      <span style="font-size: 11.5px; color: #94a3b8;">${baseVal}</span>
                      <span style="color: #64748b; font-size: 11px;">+</span>
                      <span style="font-size: 13px; font-weight: bold; color: #34d399; width: 22px; text-align: center;">${allocVal}</span>
                      <button data-stat-minus="${key}" class="cc-stat-btn cc-touch-btn" style="
                        background: #0f172a; border: 1px solid #475569; color: #fff;
                        width: 28px; height: 28px; border-radius: 4px; cursor: pointer; font-weight: bold; font-size: 14px;
                        display: flex; align-items: center; justify-content: center;
                      ">-</button>
                      <button data-stat-plus="${key}" class="cc-stat-btn cc-touch-btn" style="
                        background: #0f172a; border: 1px solid #f59e0b; color: #fef08a;
                        width: 28px; height: 28px; border-radius: 4px; cursor: pointer; font-weight: bold; font-size: 14px;
                        display: flex; align-items: center; justify-content: center;
                      ">+</button>
                    </div>
                    <div style="
                      width: 44px; text-align: right; font-size: 16px; font-weight: 800; color: #fef08a; flex-shrink: 0;
                    ">${totalVal}</div>
                  </div>
                `;
              })
              .join('')}
          </div>

          <!-- Bottom Action Buttons -->
          <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 4px;">
            <button id="btn-reset-stats" class="cc-touch-btn" style="
              background: #0f172a; border: 1px solid #475569; color: #cbd5e1;
              padding: 8px 14px; border-radius: 6px; cursor: pointer; font-size: 12px; min-height: 38px;
            ">스탯 초기화</button>
            <button id="btn-recommend-stats" class="cc-touch-btn" style="
              background: #1e3a8a; border: 1px solid #3b82f6; color: #fff;
              padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 12px; font-weight: bold; min-height: 38px;
            ">💡 추천 배분 적용</button>
          </div>
        </div>
      </div>
    `;

    // Hook Race Switch
    el.querySelectorAll('.race-card-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const rId = btn.getAttribute('data-race-id') as RaceId;
        if (rId) {
          this.selectedRace = rId;
          this.renderStep4RaceAndStats(el);
        }
      });
    });

    // Hook Plus / Minus
    el.querySelectorAll('[data-stat-plus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const k = btn.getAttribute('data-stat-plus') as keyof StatBlock;
        if (this.remainingBonusPoints > 0 && this.allocatedStats[k] < 15) {
          this.allocatedStats[k]++;
          this.remainingBonusPoints--;
          this.renderStep4RaceAndStats(el);
        }
      });
    });

    el.querySelectorAll('[data-stat-minus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const k = btn.getAttribute('data-stat-minus') as keyof StatBlock;
        if (this.allocatedStats[k] > 0) {
          this.allocatedStats[k]--;
          this.remainingBonusPoints++;
          this.renderStep4RaceAndStats(el);
        }
      });
    });

    // 2번 요청: 초기화 시 30pt 순수 반환
    el.querySelector('#btn-reset-stats')?.addEventListener('click', () => {
      this.allocatedStats = { str: 0, con: 0, dex: 0, qui: 0, int: 0, pie: 0, cha: 0 };
      this.remainingBonusPoints = 30;
      this.renderStep4RaceAndStats(el);
    });

    // 2번 요청: 추천 배분 버튼을 명시적으로 눌렀을 때만 30pt를 적절히 분배
    el.querySelector('#btn-recommend-stats')?.addEventListener('click', () => {
      if (this.selectedRace === 'borean') {
        this.allocatedStats = { str: 15, con: 15, dex: 0, qui: 0, int: 0, pie: 0, cha: 0 };
      } else if (this.selectedRace === 'sylvanian') {
        this.allocatedStats = { str: 0, con: 5, dex: 15, qui: 10, int: 0, pie: 0, cha: 0 };
      } else if (this.selectedRace === 'lumian') {
        this.allocatedStats = { str: 0, con: 5, dex: 0, qui: 5, int: 15, pie: 5, cha: 0 };
      } else {
        this.allocatedStats = { str: 10, con: 10, dex: 5, qui: 5, int: 0, pie: 0, cha: 0 };
      }
      this.remainingBonusPoints = 0;
      this.renderStep4RaceAndStats(el);
    });
  }

  // --------------------------------------------------------------------------
  // STEP 5: 성별 분기 및 세밀한 외형 커스터마이징 & 이름 짓기
  //  - PC: 1020px 가로 2단 / 모바일: 세로 표준화 (상단 아바타 + 하단 컨트롤)
  // --------------------------------------------------------------------------
  private renderStep5Customization(el: HTMLElement): void {
    const curRace = TERRA_ANIMA_RACES[this.selectedRace];
    const isMale = this.appearance.gender !== 'female';
    const hairList = isMale ? MALE_HAIR_STYLES : FEMALE_HAIR_STYLES;
    const facialList = isMale 
      ? FACIAL_FEATURE_OPTIONS 
      : FACIAL_FEATURE_OPTIONS.filter((f) => f.id !== 'beard' && f.id !== 'mustache');

    el.innerHTML = `
      <div class="cc-card-container cc-step5-layout" style="
        display: flex; gap: 16px; width: 100%; max-width: 1020px; min-height: 510px;
        box-sizing: border-box;
      ">
        <!-- Left / Top: Realtime Avatar Canvas Preview -->
        <div class="cc-step5-avatar" style="
          width: 330px; display: flex; flex-direction: column; align-items: center;
          justify-content: space-between; background: rgba(15, 23, 42, 0.95); border: 1.5px solid #d97706;
          border-radius: 10px; padding: 14px; box-sizing: border-box; gap: 10px;
        ">
          <div style="font-size: 13.5px; font-weight: bold; color: #fde047; display: flex; justify-content: space-between; width: 100%;">
            <span>캐릭터 전신 아바타</span>
            <span style="font-size: 12px; font-weight: bold; color: ${isMale ? '#60a5fa' : '#f472b6'};">
              ${isMale ? '♂ 남성' : '♀ 여성'}
            </span>
          </div>

          <!-- Canvas Preview Stage -->
          <div class="cc-step5-canvas-wrap" style="
            width: 220px; height: 260px; background: radial-gradient(circle, #1e293b 0%, #090d16 85%);
            border: 2px solid #f59e0b; border-radius: 12px; display: flex; align-items: center; justify-content: center;
            box-shadow: 0 0 20px rgba(0, 0, 0, 0.8), inset 0 0 20px rgba(245, 158, 11, 0.15);
            position: relative; overflow: hidden;
          ">
            <canvas id="char-preview-canvas" class="cc-step5-canvas" width="220" height="260" style="image-rendering: pixelated;"></canvas>
            
            <div style="
              position: absolute; bottom: 8px; left: 8px; right: 8px;
              background: rgba(15, 23, 42, 0.88); border: 1px solid #334155; border-radius: 4px;
              padding: 4px 6px; text-align: center; font-size: 10.5px; color: #fde047;
            ">
              ${isMale ? '기본 복식: 황토색 팬티' : '기본 복식: 황토색 브라탑 & 팬티'}
            </div>
          </div>

          <div style="text-align: center; width: 100%;">
            <div style="font-size: 16px; font-weight: bold; color: #f8fafc;" id="preview-name-label">
              ${this.characterName || '무명의 모험가'}
            </div>
            <div style="font-size: 11px; color: #94a3b8; margin-top: 2px;">
              [${curRace.nameKr}] · 무직 (성인식 직후) · 황토색 기본 의복
            </div>
          </div>

          <button id="btn-random-appearance" class="cc-touch-btn" style="
            background: #1e293b; border: 1px solid #475569; color: #cbd5e1;
            padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 12.5px; width: 100%; max-width: 220px;
          ">🎲 외형 랜덤 생성</button>
        </div>

        <!-- Right / Bottom: Appearance Controls & Name Input -->
        <div class="cc-step5-controls" style="
          flex: 1; display: flex; flex-direction: column; justify-content: space-between;
          background: rgba(15, 23, 42, 0.95); border: 1px solid #334155;
          border-radius: 10px; padding: 16px 18px; box-sizing: border-box; gap: 12px;
        ">
          <!-- Top Row: Name Input & Gender Selection -->
          <div class="cc-step5-name-gender" style="display: flex; gap: 12px; align-items: stretch;">
            <!-- Name Check Input -->
            <div style="flex: 1; background: #1e293b; padding: 10px 12px; border-radius: 6px; border: 1px solid #334155;">
              <label style="display: block; font-size: 12px; font-weight: bold; color: #fde047; margin-bottom: 5px;">
                캐릭터 이름 (2~12글자)
              </label>
              <div style="display: flex; gap: 6px;">
                <input id="input-char-name" type="text" value="${this.characterName}" maxlength="12" style="
                  flex: 1; background: #090d16; border: 1px solid #475569; color: #fff;
                  padding: 8px 10px; border-radius: 4px; font-size: 14px;
                " />
                <button id="btn-check-name" class="cc-touch-btn" style="
                  background: #10b981; border: none; color: #fff; font-weight: bold;
                  padding: 8px 14px; border-radius: 4px; cursor: pointer; font-size: 13px; flex-shrink: 0;
                ">중복 확인</button>
              </div>
              <div id="name-status-msg" style="font-size: 11px; color: #fbbf24; margin-top: 4px;">
                ⚠ 이름 입력 후 [중복 확인]을 눌러주세요.
              </div>
            </div>

            <!-- Gender Picker (남성 / 여성) -->
            <div class="cc-step5-gender-box" style="width: 210px; background: #1e293b; padding: 10px 12px; border-radius: 6px; border: 1px solid #334155; display: flex; flex-direction: column; justify-content: space-between;">
              <label style="display: block; font-size: 12px; font-weight: bold; color: #fde047; margin-bottom: 5px;">
                성별 선택 (Gender)
              </label>
              <div style="display: flex; gap: 6px; height: 38px;">
                <button id="btn-gender-male" class="cc-touch-btn" style="
                  flex: 1; border-radius: 4px; font-size: 13px; font-weight: bold; cursor: pointer;
                  background: ${isMale ? '#2563eb' : '#0f172a'};
                  color: ${isMale ? '#ffffff' : '#94a3b8'};
                  border: 1.5px solid ${isMale ? '#60a5fa' : '#334155'};
                ">♂ 남성</button>
                <button id="btn-gender-female" class="cc-touch-btn" style="
                  flex: 1; border-radius: 4px; font-size: 13px; font-weight: bold; cursor: pointer;
                  background: ${!isMale ? '#db2777' : '#0f172a'};
                  color: ${!isMale ? '#ffffff' : '#94a3b8'};
                  border: 1.5px solid ${!isMale ? '#f472b6' : '#334155'};
                ">♀ 여성</button>
              </div>
            </div>
          </div>

          <!-- Appearance Controls Grid -->
          <div class="cc-step5-grid" style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px 12px;">
            <!-- Hair Style -->
            <div style="background: #1e293b; padding: 8px 10px; border-radius: 6px; border: 1px solid #334155;">
              <label style="font-size: 11.5px; color: #cbd5e1; display: block; margin-bottom: 4px;">
                헤어 스타일 (${isMale ? '남성 전용' : '여성 전용'})
              </label>
              <select id="select-hair-style" style="width: 100%; background: #090d16; border: 1px solid #475569; color: #fff; padding: 6px; border-radius: 4px; font-size: 13px;">
                ${hairList.map(
                  (h: { id: string; name: string }) => `<option value="${h.id}" ${h.id === this.appearance.hairStyle ? 'selected' : ''}>${h.name}</option>`
                ).join('')}
              </select>
            </div>

            <!-- Facial Feature -->
            <div style="background: #1e293b; padding: 8px 10px; border-radius: 6px; border: 1px solid #334155;">
              <label style="font-size: 11.5px; color: #cbd5e1; display: block; margin-bottom: 4px;">
                얼굴 특징 ${isMale ? '(수염 / 흉터 / 문신)' : '(흉터 / 고대 문신)'}
              </label>
              <select id="select-facial" style="width: 100%; background: #090d16; border: 1px solid #475569; color: #fff; padding: 6px; border-radius: 4px; font-size: 13px;">
                ${facialList.map(
                  (f: { id: string; name: string }) => `<option value="${f.id}" ${f.id === this.appearance.facialFeature ? 'selected' : ''}>${f.name}</option>`
                ).join('')}
              </select>
            </div>

            <!-- Eye Angle -->
            <div style="background: #1e293b; padding: 8px 10px; border-radius: 6px; border: 1px solid #334155;">
              <label style="font-size: 11.5px; color: #cbd5e1; display: block; margin-bottom: 4px;">눈매 각도 (Eye Angle)</label>
              <select id="select-eye-angle" style="width: 100%; background: #090d16; border: 1px solid #475569; color: #fff; padding: 6px; border-radius: 4px; font-size: 13px;">
                ${EYE_ANGLE_OPTIONS.map(
                  (a) => `<option value="${a.id}" ${a.id === (this.appearance.eyeAngle || 'normal') ? 'selected' : ''}>${a.name}</option>`
                ).join('')}
              </select>
            </div>

            <!-- Eye Size -->
            <div style="background: #1e293b; padding: 8px 10px; border-radius: 6px; border: 1px solid #334155;">
              <label style="font-size: 11.5px; color: #cbd5e1; display: block; margin-bottom: 4px;">눈 크기 (Eye Size)</label>
              <select id="select-eye-size" style="width: 100%; background: #090d16; border: 1px solid #475569; color: #fff; padding: 6px; border-radius: 4px; font-size: 13px;">
                ${EYE_SIZE_OPTIONS.map(
                  (s) => `<option value="${s.id}" ${s.id === (this.appearance.eyeSize || 'medium') ? 'selected' : ''}>${s.name}</option>`
                ).join('')}
              </select>
            </div>

            <!-- Body Shape -->
            <div style="background: #1e293b; padding: 8px 10px; border-radius: 6px; border: 1px solid #334155;">
              <label style="font-size: 11.5px; color: #cbd5e1; display: block; margin-bottom: 4px;">기본 체형 (Body Shape)</label>
              <select id="select-body-shape" style="width: 100%; background: #090d16; border: 1px solid #475569; color: #fff; padding: 6px; border-radius: 4px; font-size: 13px;">
                ${BODY_SHAPE_OPTIONS.map(
                  (b) => `<option value="${b.id}" ${b.id === (this.appearance.bodyShape || 'normal') ? 'selected' : ''}>${b.name}</option>`
                ).join('')}
              </select>
            </div>

            <!-- Body Scale -->
            <div style="background: #1e293b; padding: 8px 10px; border-radius: 6px; border: 1px solid #334155;">
              <div style="display:flex; justify-content:space-between;">
                <label style="font-size: 11.5px; color: #cbd5e1; margin-bottom: 4px;">키 · 체격 비율 (Scale)</label>
                <span id="label-scale-val" style="font-size: 12px; color:#fde047; font-weight:bold;">${Math.round((this.appearance.bodyScale || 1.0) * 100)}%</span>
              </div>
              <input id="range-scale" type="range" min="88" max="115" value="${Math.round((this.appearance.bodyScale || 1.0) * 100)}" style="width: 100%; accent-color: #f59e0b; cursor: pointer; height: 20px;" />
            </div>

            <!-- Skin Color Palette -->
            <div style="background: #1e293b; padding: 8px 10px; border-radius: 6px; border: 1px solid #334155;">
              <label style="font-size: 11.5px; color: #cbd5e1; display: block; margin-bottom: 6px;">피부색 (Skin Tone)</label>
              <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                ${SKIN_COLOR_PALETTE.map(
                  (c: { name: string; hex: string }) => `
                  <div data-skin-hex="${c.hex}" class="cc-touch-btn" style="
                    width: 24px; height: 24px; border-radius: 50%; background: ${c.hex};
                    cursor: pointer; border: 2px solid ${c.hex === this.appearance.skinColor ? '#f59e0b' : '#334155'};
                  " title="${c.name}"></div>
                `
                ).join('')}
              </div>
            </div>

            <!-- Hair Color Palette -->
            <div style="background: #1e293b; padding: 8px 10px; border-radius: 6px; border: 1px solid #334155;">
              <label style="font-size: 11.5px; color: #cbd5e1; display: block; margin-bottom: 6px;">머리색 (Hair Color)</label>
              <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                ${HAIR_COLOR_PALETTE.map(
                  (c: { name: string; hex: string }) => `
                  <div data-hair-hex="${c.hex}" class="cc-touch-btn" style="
                    width: 24px; height: 24px; border-radius: 50%; background: ${c.hex};
                    cursor: pointer; border: 2px solid ${c.hex === this.appearance.hairColor ? '#f59e0b' : '#334155'};
                  " title="${c.name}"></div>
                `
                ).join('')}
              </div>
            </div>
          </div>

          <!-- Bottom: Undyed Default Underwear Notice -->
          <div style="
            background: rgba(30, 41, 59, 0.8); border: 1px dashed #d97706; border-radius: 6px;
            padding: 8px 12px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px;
          ">
            <div style="display: flex; align-items: center; gap: 8px;">
              <div style="width: 20px; height: 20px; border-radius: 4px; background: #b45309; border: 1px solid #f59e0b; flex-shrink: 0;"></div>
              <div style="font-size: 11.5px; color: #cbd5e1;">
                <span style="font-weight: bold; color: #fde047;">성인식 기본 의복:</span> 천연 황토색 속옷 (전직 전 무직)
              </div>
            </div>
            <div style="font-size: 10px; color: #94a3b8;">
              ※ 갑옷 및 전용 염료는 인게임 상점/캐시샵에서 이용 가능합니다.
            </div>
          </div>
        </div>
      </div>
    `;

    // Canvas Preview Loop
    this.startCanvasPreview(el);

    // Hook inputs
    const inputEl = el.querySelector('#input-char-name') as HTMLInputElement;
    inputEl?.addEventListener('input', () => {
      this.characterName = inputEl.value;
      this.nameChecked = false;
      const st = el.querySelector('#name-status-msg');
      if (st) {
        st.textContent = '⚠ 이름 입력 후 [중복 확인]을 눌러주세요.';
        st.setAttribute('style', 'font-size: 11px; color: #fbbf24; margin-top: 4px;');
      }
      const lbl = el.querySelector('#preview-name-label');
      if (lbl) lbl.textContent = this.characterName || '무명의 모험가';
    });

    el.querySelector('#btn-check-name')?.addEventListener('click', () => {
      const nm = this.characterName.trim();
      const statusEl = el.querySelector('#name-status-msg');
      const setStatus = (msg: string, color: string) => {
        if (!statusEl) return;
        statusEl.textContent = msg;
        statusEl.setAttribute('style', `font-size: 11px; color: ${color}; margin-top: 4px;`);
      };
      if (!nm) {
        alert('이름을 입력해주세요!');
        return;
      }
      if (nm.length < 2) {
        this.nameChecked = false;
        setStatus('✗ 이름은 2글자 이상이어야 합니다.', '#f87171');
        return;
      }
      if (AccountSaveSystem.isNameTaken(nm)) {
        this.nameChecked = false;
        setStatus(`✗ [${nm}] 은(는) 이미 사용 중인 이름입니다.`, '#f87171');
        return;
      }
      this.nameChecked = true;
      setStatus(`✓ [${nm}] 은(는) 생성 가능한 이름입니다!`, '#34d399');
    });

    // Gender Toggles
    el.querySelector('#btn-gender-male')?.addEventListener('click', () => {
      if (this.appearance.gender !== 'male') {
        this.appearance.gender = 'male';
        if (!MALE_HAIR_STYLES.some((h) => h.id === this.appearance.hairStyle)) {
          this.appearance.hairStyle = 'short';
        }
        this.renderStep5Customization(el);
      }
    });

    el.querySelector('#btn-gender-female')?.addEventListener('click', () => {
      if (this.appearance.gender !== 'female') {
        this.appearance.gender = 'female';
        this.appearance.facialFeature = 'none'; // 여자는 수염 해제
        if (!FEMALE_HAIR_STYLES.some((h) => h.id === this.appearance.hairStyle)) {
          this.appearance.hairStyle = 'long';
        }
        this.renderStep5Customization(el);
      }
    });

    el.querySelector('#select-hair-style')?.addEventListener('change', (e) => {
      this.appearance.hairStyle = (e.target as HTMLSelectElement).value as HairStyleId;
    });

    el.querySelector('#select-facial')?.addEventListener('change', (e) => {
      this.appearance.facialFeature = (e.target as HTMLSelectElement).value as FacialFeatureId;
    });

    el.querySelector('#select-eye-angle')?.addEventListener('change', (e) => {
      this.appearance.eyeAngle = (e.target as HTMLSelectElement).value as EyeAngleType;
    });

    el.querySelector('#select-eye-size')?.addEventListener('change', (e) => {
      this.appearance.eyeSize = (e.target as HTMLSelectElement).value as EyeSizeType;
    });

    el.querySelector('#select-body-shape')?.addEventListener('change', (e) => {
      this.appearance.bodyShape = (e.target as HTMLSelectElement).value as BodyShapeType;
    });

    el.querySelector('#range-scale')?.addEventListener('input', (e) => {
      const val = parseInt((e.target as HTMLInputElement).value, 10);
      this.appearance.bodyScale = val / 100;
      const lbl = el.querySelector('#label-scale-val');
      if (lbl) lbl.textContent = `${val}%`;
    });

    el.querySelector('#btn-random-appearance')?.addEventListener('click', () => {
      const currentGen = this.appearance.gender || 'male';
      this.appearance = randomizeCharacterAppearance(currentGen);
      this.appearance.showHelm = false;
      this.appearance.primaryArmorColor = '#b45309';
      this.renderStep5Customization(el);
    });

    el.querySelectorAll('[data-skin-hex]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const hex = btn.getAttribute('data-skin-hex');
        if (hex) {
          this.appearance.skinColor = hex;
          this.renderStep5Customization(el);
        }
      });
    });

    el.querySelectorAll('[data-hair-hex]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const hex = btn.getAttribute('data-hair-hex');
        if (hex) {
          this.appearance.hairColor = hex;
          this.renderStep5Customization(el);
        }
      });
    });
  }

  // --------------------------------------------------------------------------
  // 실시간 캐릭터 캔버스 렌더링 루프 (SpriteRenderer 활용 2.8x 확대 전신 드로잉)
  // --------------------------------------------------------------------------
  private startCanvasPreview(container: HTMLElement): void {
    const canvas = container.querySelector('#char-preview-canvas') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let startTime = performance.now();

    const drawFrame = (now: number) => {
      const elapsed = (now - startTime);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Create a preview dummy entity
      const dummyEntity: EntityState = {
        id: 'char_preview_dummy',
        name: this.characterName,
        category: 'player',
        role: this.selectedRole,
        job: '성인식 모험가',
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        dir: 'S',
        speed: 0,
        resources: { hp: 100, maxHp: 100, sp: 100, maxSp: 100, mp: 100, maxMp: 100, conc: 100, maxConc: 100 },
        buffs: [],
        cc: null,
        cast: null,
        isDead: false,
        color: this.appearance.primaryArmorColor,
        weaponType: 'slash',
        cooldowns: {},
        targetId: null,
        inCombat: false,
        avatarIcon: '🧙',
        appearance: this.appearance,
      };

      // 캔버스 중앙에 2.8배 크기로 세밀하게 렌더링
      this.spriteRenderer.drawCharacterPreview(
        ctx,
        dummyEntity,
        canvas.width / 2,
        canvas.height / 2 + 35,
        2.8,
        elapsed
      );

      this.previewAnimFrame = requestAnimationFrame(drawFrame);
    };

    this.previewAnimFrame = requestAnimationFrame(drawFrame);
  }

  // --------------------------------------------------------------------------
  // STEP 6: 생성 완료 확인 (PC: 820px / 모바일: 세로 표준화)
  // --------------------------------------------------------------------------
  private renderStep6Confirmation(el: HTMLElement): void {
    const profile = computePersonalityProfile(this.quizAnswers);
    const race = TERRA_ANIMA_RACES[this.selectedRace];
    const clock = WorldClock.now();

    el.innerHTML = `
      <div class="cc-card-container" style="
        width: 100%; max-width: 820px; min-height: 500px; display: flex; flex-direction: column;
        justify-content: space-between; background: rgba(15, 23, 42, 0.95); border: 2px solid #f59e0b;
        border-radius: 12px; padding: 24px 28px; box-shadow: 0 12px 40px rgba(0,0,0,0.8);
        box-sizing: border-box; gap: 14px;
      ">
        <div style="text-align: center;">
          <span style="font-size: 32px; line-height: 1;">👑</span>
          <h2 style="font-size: 21px; color: #fef08a; margin: 4px 0;">
            모험가 탄생 · 솔라리스 왕국의 축복
          </h2>
          <p style="font-size: 12px; color: #94a3b8; margin: 0;">
            새로운 영웅의 탄생을 축하합니다. 아래의 정보를 확인하고 테라 아니마 세계로 떠나십시오.
          </p>
        </div>

        <div class="cc-step6-grid" style="display: grid; grid-template-columns: 1fr 1fr; gap: 14px;">
          <!-- Basic Info Card -->
          <div style="background: #1e293b; padding: 14px 18px; border-radius: 8px; border: 1px solid #334155;">
            <div style="font-size: 13.5px; font-weight: bold; color: #fde047; margin-bottom: 6px;">
              기본 신상 정보
            </div>
            <div style="font-size: 12.5px; color: #cbd5e1; line-height: 1.7;">
              <div>• <b>이름:</b> <span style="color: #fef08a;">${this.characterName}</span> (${this.appearance.gender === 'female' ? '여성 ♀' : '남성 ♂'})</div>
              <div>• <b>종족:</b> ${race.nameKr}</div>
              <div>• <b>직업:</b> <span style="color: #94a3b8;">무직 (성내 직업 교관을 찾아가 전직)</span></div>
              <div>• <b>의복:</b> <span style="color: #d97706;">기본 황토색 ${this.appearance.gender === 'female' ? '브라탑 & 팬티' : '팬티'}</span></div>
              <div>• <b>출신지:</b> ${race.originZone}</div>
              <div>• <b>현재 월드 일시:</b> <span style="color: #34d399; font-weight: bold;">${clock.formattedTime} (${clock.timeOfDayLabel})</span></div>
            </div>
          </div>

          <!-- Personality Card -->
          <div style="background: #1e293b; padding: 14px 18px; border-radius: 8px; border: 1px solid #334155;">
            <div style="font-size: 13.5px; font-weight: bold; color: #fde047; margin-bottom: 6px;">
              영혼의 성소 성향 시험 결과
            </div>
            <div style="font-size: 12.5px; color: #cbd5e1; line-height: 1.7;">
              <div>• <b>성향 코드:</b> <span style="color: #34d399; font-weight: bold;">${profile.mbti}</span> (${profile.alignment})</div>
              <div>• <b>영혼의 칭호:</b> ${profile.soulTitle}</div>
              <div>• <b>설명:</b> ${profile.summary.slice(0, 42)}...</div>
            </div>
          </div>
        </div>

        <!-- Stats Overview -->
        <div class="cc-step6-stats" style="
          background: #090d16; padding: 12px 14px; border-radius: 8px;
          border: 1px solid #334155; display: flex; justify-content: space-around;
        ">
          ${(
            [
              ['힘 (STR)', race.baseStats.str + this.allocatedStats.str],
              ['체력 (CON)', race.baseStats.con + this.allocatedStats.con],
              ['민첩 (DEX)', race.baseStats.dex + this.allocatedStats.dex],
              ['순발 (QUI)', race.baseStats.qui + this.allocatedStats.qui],
              ['지능 (INT)', race.baseStats.int + this.allocatedStats.int],
              ['신앙 (PIE)', race.baseStats.pie + this.allocatedStats.pie],
              ['매력 (CHA)', race.baseStats.cha + this.allocatedStats.cha],
            ] as const
          )
            .map(
              ([name, val]) => `
            <div class="cc-step6-stat-item" style="text-align: center;">
              <div style="font-size: 11px; color: #94a3b8;">${name}</div>
              <div style="font-size: 16px; font-weight: 800; color: #fef08a;">${val}</div>
            </div>
          `
            )
            .join('')}
        </div>
      </div>
    `;
  }

  private finishCreation(): void {
    // 최종 단계에서 한 번 더 이름 중복 확인 (다른 탭에서 같은 이름이 생성되었을 수 있음)
    if (AccountSaveSystem.isNameTaken(this.characterName)) {
      alert('이미 사용 중인 이름입니다. 이름을 변경해주세요.');
      this.nameChecked = false;
      this.currentStep = 5;
      this.render();
      return;
    }
    const profile = computePersonalityProfile(this.quizAnswers);
    const race = TERRA_ANIMA_RACES[this.selectedRace];
    const finalStats: StatBlock = {
      str: race.baseStats.str + this.allocatedStats.str,
      con: race.baseStats.con + this.allocatedStats.con,
      dex: race.baseStats.dex + this.allocatedStats.dex,
      qui: race.baseStats.qui + this.allocatedStats.qui,
      int: race.baseStats.int + this.allocatedStats.int,
      pie: race.baseStats.pie + this.allocatedStats.pie,
      cha: race.baseStats.cha + this.allocatedStats.cha,
    };

    const result: CharacterCreationResult = {
      name: this.characterName.trim() || '아니마의모험가',
      race: this.selectedRace,
      role: 'tank', // 기본 액션/모션 베이스
      job: '무직 (초보자)',
      stats: finalStats,
      personality: profile,
      appearance: this.appearance,
    };

    this.close();
    this.onCompleteCallback?.(result);
  }
}
